import { useMemo, useState } from "react";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { Building2, ChevronDown, ChevronRight, Info, Lock, RefreshCw } from "lucide-react";

import { BenchmarkCell } from "@/components/cinch/BenchmarkCell";
import { AgChartPanel } from "@/components/cinch/charts";
import { buildAgOptions, type ChartDataset } from "@/components/cinch/charts/chartData";
import { useIsDark } from "@/components/cinch/charts/agTheme";
import { DateRangePicker, type DateRange } from "@/components/cinch/DatePicker";
import { DeltaCell } from "@/components/cinch/DeltaCell";
import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { KpiTile } from "@/components/cinch/KpiTile";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useCan } from "@/lib/auth";
import { cn } from "@/lib/utils";

import type { BvaSearch } from "..";
import {
  useBvaDefinitions,
  useBvaDepartment,
  useBvaRefresh,
  useBvaSummary,
  useBvaTransactions,
  type AccountBlock,
  type CategoryBlock,
  type ClassBlock,
  type GlossaryDef,
  type SummaryResponse,
  type Ytd,
} from "../api";
import {
  chartWindow,
  MONTH_LABELS,
  overBudget,
  PERIOD_OPTIONS,
  periodWindow,
  spendPct,
  unbudgeted,
  windowLabel,
  windowTotals,
  type BvaPeriod,
  type MonthWindow,
} from "../lib";
import { AccessConfigTab } from "../components/AccessConfigTab";
import { fmtCur, fmtPct } from "../../renewals/format";

export function BudgetVsActualPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/budget-vs-actual" }) as BvaSearch;
  const canAdmin = useCan("budget_vs_actual.admin");
  const tab = search.tab === "access" && canAdmin ? "access" : "dashboard";
  const year = search.year ?? new Date().getFullYear();
  const [period, setPeriod] = useState<BvaPeriod>("CLOSED_YTD");
  const [customRange, setCustomRange] = useState<DateRange>({ start: null, end: null });

  const setSearch = (next: Partial<BvaSearch>) =>
    navigate({
      to: "/budget-vs-actual",
      search: (prev: BvaSearch) => ({ ...prev, ...next }),
    });

  const summary = useBvaSummary(year, false);
  const refresh = useBvaRefresh(year);

  const data = summary.data;

  // Custom dates → month indices, clamped into the fiscal year.
  const customWindow = useMemo<MonthWindow | null>(() => {
    if (!customRange.start || !customRange.end) return null;
    const idx = (d: Date) =>
      d.getFullYear() < year ? 0 : d.getFullYear() > year ? 11 : d.getMonth();
    return { start: idx(customRange.start), end: idx(customRange.end) };
  }, [customRange, year]);
  const window = periodWindow(period, data?.complete_months ?? 0, customWindow);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Budget vs Actual</h1>
          <p className="text-sm text-muted-foreground">
            {year} spend and revenue against the QuickBooks budget, by department
            (QB class).
          </p>
        </div>
        {data && (
          <div className="flex items-center gap-4">
            <div className="text-right text-xs text-muted-foreground">
              Budget: <b className="text-foreground">{data.budget.name}</b>
              {data.budget.source === "override" && " (pinned)"}
              <br />
              Refreshed {new Date(data.refreshed_at).toLocaleString()}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
            >
              <RefreshCw className={cn("mr-1 h-3.5 w-3.5", refresh.isPending && "animate-spin")} />
              Refresh
            </Button>
          </div>
        )}
      </div>

      {data && <ScopeBanner scope={data.scope} />}

      {canAdmin && (
        <div className="flex flex-wrap gap-1 border-b border-border">
          {(["dashboard", "access"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setSearch({ tab: t === "dashboard" ? undefined : t })}
              className={cn(
                "rounded-t-md border-b-2 px-3 py-1.5 text-sm font-medium capitalize",
                tab === t
                  ? "border-brand text-brand"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {t === "access" ? "Access & Budget" : "Dashboard"}
            </button>
          ))}
        </div>
      )}

      {tab === "access" ? (
        <AccessConfigTab year={year} />
      ) : (
        <DashboardTab
          data={data}
          loading={summary.isLoading}
          error={summary.error ? String(summary.error) : null}
          period={period}
          setPeriod={setPeriod}
          customRange={customRange}
          setCustomRange={setCustomRange}
          window={window}
          dept={search.dept ?? null}
          setDept={(d) => setSearch({ dept: d ?? undefined, view: undefined })}
        />
      )}

      {/* Department drawer: quick view = rolled-up category lines; the expand
          chevron grows it into the full drill-down (categories → accounts →
          GL lines). URL-backed so a dept/view deep-link restores it. */}
      <DepartmentDrawer
        year={year}
        className={search.dept ?? null}
        window={window}
        expanded={search.view === "full"}
        onExpandedChange={(v) => setSearch({ view: v ? "full" : undefined })}
        onClose={() => setSearch({ dept: undefined, view: undefined })}
      />
    </div>
  );
}

// ─── Scope banner ─────────────────────────────────────────────────────

/** Says out loud what slice of the company the numbers below cover, so a
 *  department lead never mistakes their limited view for the whole picture. */
function ScopeBanner({ scope }: { scope: SummaryResponse["scope"] }) {
  if (scope.all_classes) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
        <Building2 className="h-4 w-4 shrink-0 text-brand" />
        <span className="font-medium">Company-wide view</span>
        <span className="text-muted-foreground">
          — all departments, revenue, and company totals.
        </span>
      </div>
    );
  }
  const hasDepts = scope.classes.length > 0;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
      <Lock className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="font-medium">Limited view</span>
      <span className="text-muted-foreground">— you're seeing</span>
      {hasDepts ? (
        scope.classes.map((c) => (
          <Badge key={c} variant="neutral">
            {c}
          </Badge>
        ))
      ) : (
        <span className="text-muted-foreground">no departments</span>
      )}
      {scope.include_revenue && (
        <>
          {hasDepts && <span className="text-muted-foreground">plus</span>}
          <Badge variant="info">Revenue</Badge>
        </>
      )}
      <span className="text-muted-foreground">
        {hasDepts || scope.include_revenue
          ? "— totals below cover only this slice, not the whole company."
          : "— ask an admin for a department grant."}
      </span>
    </div>
  );
}

// ─── Period filter bar (mirrors the KPI dashboard's PeriodSelector) ───

function PeriodBar({
  value,
  onChange,
  rangeLabel,
  customRange,
  onCustomRangeChange,
}: {
  value: BvaPeriod;
  onChange: (next: BvaPeriod) => void;
  rangeLabel: string;
  customRange: DateRange;
  onCustomRangeChange: (next: DateRange) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Period
        </span>
        {PERIOD_OPTIONS.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={cn(
                "rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors",
                active
                  ? "border-brand bg-brand text-white"
                  : "border-border bg-card text-muted-foreground hover:border-brand hover:text-brand",
              )}
            >
              {opt.label}
            </button>
          );
        })}
        <span className="ml-auto text-xs text-muted-foreground">{rangeLabel}</span>
      </div>
      {value === "CUSTOM" && (
        <div className="flex flex-wrap items-center gap-2">
          <DateRangePicker value={customRange} onChange={onCustomRangeChange} />
          <span className="text-[11px] text-muted-foreground">
            Snaps to calendar months within the selected year
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Dashboard ───────────────────────────────────────────────────────

function DashboardTab({
  data,
  loading,
  error,
  period,
  setPeriod,
  customRange,
  setCustomRange,
  window,
  dept,
  setDept,
}: {
  data: SummaryResponse | undefined;
  loading: boolean;
  error: string | null;
  period: BvaPeriod;
  setPeriod: (p: BvaPeriod) => void;
  customRange: DateRange;
  setCustomRange: (r: DateRange) => void;
  window: MonthWindow;
  dept: string | null;
  setDept: (d: string | null) => void;
}) {
  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (error) {
    return (
      <Alert variant="danger">
        <AlertTitle>Couldn't load budget data</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }
  if (!data) return null;

  const expense = data.expense;
  const noScope = !data.scope.all_classes && data.scope.classes.length === 0;
  if (noScope && !data.scope.include_revenue) {
    return (
      <Alert>
        <AlertTitle>No departments granted yet</AlertTitle>
        <AlertDescription>
          You have access to the page but no department has been assigned to you.
          Ask an admin to grant your department under Access &amp; Budget.
        </AlertDescription>
      </Alert>
    );
  }

  // Window-scoped aggregates: the API ships full-year monthly arrays; every
  // period preset is a client-side sum, so switching periods is instant.
  const classTotals = expense.classes
    .map((c) => ({ block: c, totals: windowTotals(c, window) }))
    .sort((a, b) => b.totals.variance - a.totals.variance);
  const spendTotal = windowTotals(expense.total, window);
  const revenueTotal = data.revenue ? windowTotals(data.revenue.total, window) : null;
  const over = classTotals.filter((c) => overBudget(c.totals));
  const label = windowLabel(window);

  return (
    <div className="flex flex-col gap-6">
      <PeriodBar
        value={period}
        onChange={setPeriod}
        rangeLabel={`${label} · ${data.year}`}
        customRange={customRange}
        onCustomRangeChange={setCustomRange}
      />

      {/* KPI band */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <KpiTile
          label={expense.total_is_company ? "Total spend" : "My depts spend"}
          value={fmtCur(spendTotal.actual)}
          sub={<>of {fmtCur(spendTotal.budget)} budget · {label}</>}
        />
        <KpiTile
          label="Spend variance"
          value={<DeltaCell value={spendTotal.variance} invert className="text-2xl" />}
          tone={spendTotal.variance > 0 ? "danger" : "positive"}
          sub={
            spendTotal.pct != null && (
              <>{fmtPct(Math.abs(spendTotal.pct))} {spendTotal.variance > 0 ? "over" : "under"} budget</>
            )
          }
        />
        {revenueTotal && (
          <KpiTile
            label="Revenue"
            value={fmtCur(revenueTotal.actual)}
            sub={<>of {fmtCur(revenueTotal.budget)} budget · {label}</>}
          />
        )}
        {revenueTotal && (
          <KpiTile
            label="Revenue variance"
            value={<DeltaCell value={revenueTotal.variance} className="text-2xl" />}
            tone={revenueTotal.variance < 0 ? "danger" : "positive"}
            sub={
              revenueTotal.pct != null && (
                <>{fmtPct(Math.abs(revenueTotal.pct))} {revenueTotal.variance < 0 ? "behind" : "ahead of"} budget</>
              )
            }
          />
        )}
        <KpiTile
          label="Over budget"
          value={over.length === 0 ? "None" : `${over.length} dept${over.length > 1 ? "s" : ""}`}
          tone={over.length > 0 ? "danger" : "positive"}
          sub={
            over.length > 0 && (
              <>
                {over
                  .slice(0, 2)
                  .map((c) => `${c.block.class_name} +${fmtCur(c.totals.variance)}`)
                  .join(" · ")}
              </>
            )
          }
        />
      </div>

      {/* Department cards, worst first within the selected window */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {classTotals.map(({ block, totals }) => (
          <DeptCard
            key={block.class_name}
            block={block}
            totals={totals}
            selected={dept === block.class_name}
            onClick={() => setDept(dept === block.class_name ? null : block.class_name)}
          />
        ))}
      </div>

      {/* Trends: spend, then revenue when in scope. Long windows draw the
          cumulative race to plan; short windows (< 4 months) switch to
          month-by-month budget/actual columns. */}
      <TrendCard
        chartKey="bva-spend-trend"
        title={data.expense.total_is_company ? "Company spend" : "My departments"}
        total={data.expense.total}
        period={period}
        window={window}
      />
      {data.revenue && (
        <TrendCard
          chartKey="bva-revenue-trend"
          title="Revenue"
          total={data.revenue.total}
          period={period}
          window={window}
        />
      )}

      {/* Revenue to budget */}
      {data.revenue && <RevenueCard data={data} window={window} />}
    </div>
  );
}

function DeptCard({
  block,
  totals,
  selected,
  onClick,
}: {
  block: ClassBlock;
  totals: Ytd;
  selected: boolean;
  onClick: () => void;
}) {
  const over = overBudget(totals);
  const pct = spendPct(totals);
  return (
    <KpiTile
      label={block.class_name}
      value={<DeltaCell value={totals.variance} invert className="text-2xl" />}
      onClick={onClick}
      selected={selected}
      sub={
        <div className="flex items-center justify-between gap-2">
          <span>
            {fmtCur(totals.actual)} of {fmtCur(totals.budget)}
          </span>
          {/* Over budget earns the one SOLID chip on the page (DS emphasis
              rule); everything calm stays light-tinted. */}
          {unbudgeted(totals, block.fy_budget) ? (
            <Badge variant="warning">Unbudgeted</Badge>
          ) : over ? (
            <Badge variant="danger-solid">{totals.pct != null ? `+${fmtPct(totals.pct)}` : "Over"}</Badge>
          ) : (
            <Badge variant="success">{totals.pct != null ? fmtPct(totals.pct) : "Under"}</Badge>
          )}
        </div>
      }
      progress={{ pct, barClass: over ? "bg-danger" : "bg-brand" }}
    />
  );
}

function TrendCard({
  chartKey,
  title,
  total,
  period,
  window,
}: {
  chartKey: string;
  /** Card title prefix, e.g. "Company spend" — the mode + range is appended. */
  title: string;
  /** The class block whose monthly budget/actual series get plotted. */
  total: ClassBlock;
  period: BvaPeriod;
  window: MonthWindow;
}) {
  const isDark = useIsDark();

  // Budget draws to the period's natural end (December for YTD, quarter end
  // for QTD); actuals stop at the window end. Short spans (< 4 months —
  // last month, QTD, short custom ranges) don't have enough points for a
  // cumulative line to mean anything, so they render as month-by-month
  // budget/actual columns instead.
  const cw = chartWindow(period, window);
  const start = Math.max(0, cw.start);
  const end = Math.min(cw.budgetEnd, 11);
  const useBars = end - start + 1 < 4;

  const dataset = useMemo<ChartDataset>(() => {
    const rows: Record<string, string | number | null>[] = [];
    let budgetSum = 0;
    let actualSum = 0;
    for (let i = start; i <= end; i++) {
      budgetSum += total.monthly.budget[i] ?? 0;
      const inActuals = i <= cw.end;
      if (inActuals) actualSum += total.monthly.actual[i] ?? 0;
      rows.push(
        useBars
          ? {
              month: MONTH_LABELS[i] as string,
              budget: Math.round((total.monthly.budget[i] ?? 0) * 100) / 100,
              actual: inActuals
                ? Math.round((total.monthly.actual[i] ?? 0) * 100) / 100
                : null,
            }
          : {
              month: MONTH_LABELS[i] as string,
              budget: Math.round(budgetSum * 100) / 100,
              actual: inActuals ? Math.round(actualSum * 100) / 100 : null,
            },
      );
    }
    return {
      key: chartKey,
      label: title,
      kind: "timeseries",
      chartTypes: useBars ? ["bar"] : ["line"],
      categoryKey: "month",
      rows,
      series: useBars
        ? [
            { key: "budget", name: "Budget" },
            { key: "actual", name: "Actual" },
          ]
        : [
            { key: "budget", name: "Budget (cumulative)" },
            { key: "actual", name: "Actual (cumulative)" },
          ],
      colors: [],
      valueFormat: (n) => fmtCur(n),
    };
  }, [chartKey, title, total, start, end, cw.end, useBars]);

  const mode = useBars ? "budget vs actual by month" : "cumulative vs budget";
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          {title} — {mode} ({windowLabel(window)})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <AgChartPanel
          options={buildAgOptions(dataset, useBars ? "bar" : "line", isDark)}
          className="h-[30rem]"
        />
      </CardContent>
    </Card>
  );
}

function RevenueCard({ data, window }: { data: SummaryResponse; window: MonthWindow }) {
  if (!data.revenue) return null;
  const rows = data.revenue.classes
    .map((c) => ({ block: c, totals: windowTotals(c, window) }))
    .sort((a, b) => b.totals.budget - a.totals.budget);
  const total = windowTotals(data.revenue.total, window);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Revenue to budget ({windowLabel(window)})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-2 pr-4">Class</th>
                <th className="py-2 pr-4 text-right">Budget</th>
                <th className="py-2 pr-4 text-right">Actual</th>
                <th className="py-2 pr-4 text-right">Variance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ block, totals }) => (
                <tr key={block.class_name} className="border-b border-border/60">
                  <td className="py-2 pr-4">{block.class_name}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{fmtCur(totals.budget)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{fmtCur(totals.actual)}</td>
                  <td className="py-2 pr-4 text-right">
                    <DeltaCell value={totals.variance} />
                  </td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td className="py-2 pr-4">Total</td>
                <td className="py-2 pr-4 text-right tabular-nums">{fmtCur(total.budget)}</td>
                <td className="py-2 pr-4 text-right tabular-nums">{fmtCur(total.actual)}</td>
                <td className="py-2 pr-4 text-right">
                  <DeltaCell value={total.variance} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Department drill-down (quick view drawer ↔ expanded page) ───────

function DepartmentDrawer({
  year,
  className,
  window,
  expanded,
  onExpandedChange,
  onClose,
}: {
  year: number;
  className: string | null;
  window: MonthWindow;
  expanded: boolean;
  onExpandedChange: (v: boolean) => void;
  onClose: () => void;
}) {
  const dept = useBvaDepartment(year, className, false);
  const data = dept.data;
  // Glossary map for the expanded view's line-item tooltips (Cody curates the
  // definitions in the Glossary; matching is by term name or alias).
  const defs = useBvaDefinitions(expanded && className != null);
  const defFor = (name: string): GlossaryDef | null =>
    defs.data?.definitions[name.trim().toLowerCase()] ?? null;

  // Window-scoped category totals, expenses first, worst first — and "worst"
  // flips sign for revenue, where under budget is the bad end.
  const categories = useMemo(
    () =>
      (data?.categories ?? [])
        .map((cat) => ({ cat, totals: windowTotals(cat, window) }))
        .sort((a, b) =>
          a.cat.kind !== b.cat.kind
            ? a.cat.kind === "Expense"
              ? -1
              : 1
            : a.cat.kind === "Expense"
              ? b.totals.variance - a.totals.variance
              : a.totals.variance - b.totals.variance,
        ),
    [data, window],
  );

  const totals = useMemo(() => {
    const expense = categories.filter((c) => c.cat.kind === "Expense");
    const budget = expense.reduce((s, c) => s + c.totals.budget, 0);
    const actual = expense.reduce((s, c) => s + c.totals.actual, 0);
    return { budget, actual, variance: actual - budget };
  }, [categories]);

  const label = windowLabel(window);

  return (
    <EntityDrawer
      open={className != null}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      title={`${className ?? ""} — budget vs actual`}
      subtitle={`${label} ${year} · spend ${fmtCur(totals.actual)} of ${fmtCur(totals.budget)}`}
      onClose={onClose}
    >
      <div className="flex flex-col gap-3 p-4">
        {dept.isLoading && <Skeleton className="h-40" />}
        {dept.error && (
          <Alert variant="danger">
            <AlertDescription>{String(dept.error)}</AlertDescription>
          </Alert>
        )}
        {data && (
          <>
            <div className="grid grid-cols-3 gap-2">
              <KpiTile label={`Budget (${label})`} value={fmtCur(totals.budget)} />
              <KpiTile label={`Actual (${label})`} value={fmtCur(totals.actual)} />
              {/* Expense categories only — a department carrying revenue lines
                  would otherwise read this as a net number. */}
              <KpiTile
                label="Spend variance"
                value={<DeltaCell value={totals.variance} invert className="text-2xl" />}
                tone={totals.variance > 0 ? "danger" : "positive"}
              />
            </div>
            {expanded ? (
              // Full function: categories expand to accounts, accounts to the
              // month grid, month cells to the GL lines behind them.
              <div className="flex flex-col gap-2">
                {categories.map(({ cat, totals: catTotals }) => (
                  <CategoryRow
                    key={`${cat.kind}:${cat.category}`}
                    cat={cat}
                    totals={catTotals}
                    window={window}
                    completeMonths={data.complete_months}
                    className={className ?? ""}
                    year={year}
                    definition={defFor(cat.category)}
                  />
                ))}
              </div>
            ) : (
              // Quick view: category line items fully rolled up — scan the
              // damage, expand (chevrons in the header) for the why.
              <QuickCategoryTable categories={categories} />
            )}
            {categories.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No activity or budget this year.
              </p>
            )}
          </>
        )}
      </div>
    </EntityDrawer>
  );
}

function QuickCategoryTable({
  categories,
}: {
  categories: { cat: CategoryBlock; totals: Ytd }[];
}) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2">Category</th>
            <th className="px-3 py-2 text-right">Budget</th>
            <th className="px-3 py-2 text-right">Actual</th>
            <th className="px-3 py-2 text-right">Variance</th>
          </tr>
        </thead>
        <tbody>
          {categories.map(({ cat, totals }) => (
            <tr key={`${cat.kind}:${cat.category}`} className="border-b border-border/60">
              <td className="px-3 py-2">
                {cat.category}
                {cat.kind === "Revenue" && (
                  <Badge variant="info" className="ml-2">
                    Revenue
                  </Badge>
                )}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{fmtCur(totals.budget)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{fmtCur(totals.actual)}</td>
              <td className="px-3 py-2 text-right">
                <DeltaCell value={totals.variance} invert={cat.kind === "Expense"} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Hover tooltip explaining what a P&L line item encompasses, sourced from
 *  the Glossary. Clicking the icon jumps to the term; when no term exists yet
 *  the tooltip says what to add so curation is self-explanatory. */
function GlossaryInfo({ name, definition }: { name: string; definition: GlossaryDef | null }) {
  const navigate = useNavigate();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role={definition ? "link" : undefined}
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            if (definition) void navigate({ to: "/glossary", hash: definition.slug });
          }}
          className={cn(
            "ml-1.5 inline-flex shrink-0 align-middle",
            definition
              ? "text-muted-foreground hover:text-brand"
              : "text-muted-foreground/40 hover:text-muted-foreground",
          )}
        >
          <Info className="h-3.5 w-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs">
        {definition ? (
          <>
            <p className="text-xs">{definition.definition}</p>
            <p className="mt-1 text-[11px] opacity-70">Click to open in the Glossary</p>
          </>
        ) : (
          <p className="text-xs">
            No glossary entry yet — add a term named "{name}" (or alias it) in the
            Glossary and its definition will show here.
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function CategoryRow({
  cat,
  totals,
  window,
  completeMonths,
  className,
  year,
  definition,
}: {
  cat: CategoryBlock;
  totals: Ytd;
  window: MonthWindow;
  /** Months closed in the books — the month grid can only judge these. */
  completeMonths: number;
  className: string;
  year: number;
  definition: GlossaryDef | null;
}) {
  const [open, setOpen] = useState(false);
  // Worst first — which means most *over* for expenses, most *under* for
  // revenue, since the two read variance in opposite directions.
  const accounts = useMemo(
    () =>
      cat.accounts
        .map((a) => ({ account: a, totals: windowTotals(a, window) }))
        .sort((a, b) =>
          cat.kind === "Expense"
            ? b.totals.variance - a.totals.variance
            : a.totals.variance - b.totals.variance,
        ),
    [cat, window],
  );
  return (
    <div className="rounded-md border">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-muted/40"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="flex-1 font-medium">
          {cat.category}
          <GlossaryInfo name={cat.category} definition={definition} />
          {cat.kind === "Revenue" && (
            <Badge variant="info" className="ml-2">
              Revenue
            </Badge>
          )}
        </span>
        <span className="hidden text-xs text-muted-foreground sm:inline">
          {fmtCur(totals.actual)} of {fmtCur(totals.budget)}
        </span>
        <DeltaCell value={totals.variance} invert={cat.kind === "Expense"} />
      </button>
      {open && (
        <div className="border-t px-3 pb-3">
          {accounts.map(({ account, totals: acctTotals }) => (
            <AccountRow
              key={account.account_number ?? account.account_name ?? "?"}
              account={account}
              totals={acctTotals}
              kind={cat.kind}
              completeMonths={completeMonths}
              className={className}
              year={year}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AccountRow({
  account,
  totals,
  kind,
  completeMonths,
  className,
  year,
}: {
  account: AccountBlock;
  totals: Ytd;
  /** Parent category's kind — decides which direction of variance is good
   *  news. Over budget is red on an expense account, green on a revenue one. */
  kind: CategoryBlock["kind"];
  /** Months closed in the books. Later months hold partial (or no) actuals,
   *  so their cells stay untinted rather than reading as a huge underspend. */
  completeMonths: number;
  className: string;
  year: number;
}) {
  const [open, setOpen] = useState(false);
  const [txnMonth, setTxnMonth] = useState<number | null>(null);
  return (
    <div className="mt-2 rounded border border-border/60">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-muted/40"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="flex-1 truncate" title={account.account_name ?? undefined}>
          {account.account_name ?? account.account_number}
        </span>
        <span className="hidden text-xs text-muted-foreground sm:inline">
          {fmtCur(totals.actual)} of {fmtCur(totals.budget)}
        </span>
        <DeltaCell value={totals.variance} invert={kind === "Expense"} />
      </button>
      {open && (
        <div className="overflow-x-auto border-t px-2 py-2">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1 pr-2 font-medium" />
                {MONTH_LABELS.map((m) => (
                  <th key={m} className="px-1.5 py-1 text-right font-medium">
                    {m}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="tabular-nums">
              <tr>
                <td className="py-0.5 pr-2 text-muted-foreground">Budget</td>
                {account.monthly.budget.map((v, i) => (
                  <td key={i} className="px-1.5 py-0.5 text-right">
                    {v ? fmtCur(v) : "—"}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="py-0.5 pr-2 text-muted-foreground">Actual</td>
                {account.monthly.actual.map((v, i) => (
                  <td key={i} className="px-1.5 py-0.5 text-right">
                    {v ? (
                      <BenchmarkCell
                        value={v}
                        benchmark={account.monthly.budget[i] ?? null}
                        invert={kind === "Expense"}
                        pending={i >= completeMonths}
                        format={fmtCur}
                        label={`${MONTH_LABELS[i]} actual`}
                        actionHint="click for GL lines"
                        onClick={() => setTxnMonth(txnMonth === i ? null : i)}
                        className="font-medium hover:text-brand"
                      />
                    ) : (
                      "—"
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          {txnMonth != null && account.account_number && (
            <TransactionsPanel
              className={className}
              account={account.account_number}
              accountLabel={account.account_name ?? account.account_number}
              monthIndex={txnMonth}
              year={year}
            />
          )}
        </div>
      )}
    </div>
  );
}

function TransactionsPanel({
  className,
  account,
  accountLabel,
  monthIndex,
  year,
}: {
  className: string;
  account: string;
  accountLabel: string;
  monthIndex: number;
  year: number;
}) {
  const period = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
  const txns = useBvaTransactions(className, account, period);
  return (
    <div className="mt-2 rounded bg-muted/40 p-2">
      <div className="mb-1 text-xs font-medium">
        {accountLabel} — {MONTH_LABELS[monthIndex]} GL lines
      </div>
      {txns.isLoading && <Skeleton className="h-16" />}
      {txns.data && txns.data.transactions.length === 0 && (
        <p className="text-xs text-muted-foreground">No transactions.</p>
      )}
      {txns.data && txns.data.transactions.length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 pr-2 font-medium">Date</th>
              <th className="py-1 pr-2 font-medium">Type</th>
              <th className="py-1 pr-2 font-medium">Counterparty</th>
              <th className="py-1 pr-2 font-medium">Memo</th>
              <th className="py-1 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {txns.data.transactions.map((t, i) => (
              <tr key={i} className="border-t border-border/40">
                <td className="py-1 pr-2 whitespace-nowrap">{t.transaction_date}</td>
                <td className="py-1 pr-2">{t.transaction_type ?? "—"}</td>
                <td className="py-1 pr-2">{t.counterparty_name ?? "—"}</td>
                <td className="max-w-[24rem] truncate py-1 pr-2" title={t.memo ?? undefined}>
                  {t.memo ?? "—"}
                </td>
                <td className="py-1 text-right">{fmtCur(t.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
